import axios from 'axios'
import URLS from './urls'
import { AbstractAuth } from '@/dashboards/common'
import { AuthStore, Channel } from 'frontendcommon-3'
import * as Sentry from '@sentry/vue'

function getSessionId (organizationUuid) {
  let sessionId = sessionStorage.getItem('customSessionId')
  if (!sessionId) {
    sessionId = organizationUuid + Date.now()
    sessionStorage.setItem('customSessionId', sessionId)
  }
  return sessionId
}

const sentryEnv = process.env.VUE_APP_SENTRY_ENV
export default class Auth extends AbstractAuth {
  onTokenUnauthorized () {
    this.redirectLogout()
  }

  redirectLogin ({ loginUrl, domain } = { loginUrl: '', domain: '' }) {
    const event = new CustomEvent('hide-content')
    window.dispatchEvent(event)
    let url = loginUrl || URLS.LOGIN_URL
    if (domain) {
      url = url + '?domain=' + domain
    }
    window.location.assign(url)
    // console.log(url)
  }

  async redirectLogout (detail) {
    const event = new CustomEvent('hide-content')
    window.dispatchEvent(event)
    const domain = detail?.domain
    let logOutUrl = URLS.LOGOUT_URL
    try {
      if (domain) {
        logOutUrl = logOutUrl + '?domain=' + domain
      }
      await axios.create().get(logOutUrl, { withCredentials: true })
    } catch {
      console.log('error logout')
    } finally {
      this.redirectLogin({ loginUrl: '', domain })
    }
    // console.log('logout url')
  }

  async fetchToken () {
    try {
      const { data } = await axios.create().get(URLS.NEXTERA_TOKEN_URL, { withCredentials: true })
      return data
    } catch (error) {
      return { authorization_url: error.response.data }
    }
  }

  async fetchShellConfig (token, removeConfigCache = false) {
    try {
      const qs = removeConfigCache ? '?sidebar_v2=true&remove-cache=true' : '?sidebar_v2=true'
      const { data } = await axios.create().get(URLS.SHELL_CONFIG_URL + qs, {
        headers: {
          Authorization: this.getAuthHeader(token)
        },
        withCredentials: true
      })
      const mergedConfig = await this.mergeLocalConfig(data)
      return this.resolveActiveItem(mergedConfig)
    } catch (error) {
      return { micros: [], sidebar: [] }
    }
  }

  async newToken (tokenType) {
    const { data } = await axios.create().put(URLS.NEW_TOKEN_URL.replace('env', tokenType), {}, { withCredentials: true })
    this.scheduleRefreshToken(data, tokenType)
    Channel.emitTokenRefreshEnd({ tokenType, tokens: data })
    return { tokenType, tokens: data }
  }

  async refreshToken (tokens, tokenType) {
    try {
      const { data } = await axios.create().put(URLS.REFRESH_TOKEN_URL.replace('env', tokenType), {}, { withCredentials: true })
      this.scheduleRefreshToken(data, tokenType)
      Channel.emitTokenRefreshEnd({ tokenType, tokens: data })
      return { tokenType, tokens: data }
    } catch (error) {
      this.redirectLogout()
      return false
    }
  }

  async scheduleRefreshToken (tokens, tokenType) {
    const expTime = tokens.expires * 1000
    const now = Date.now()
    const refreshTime = expTime - now
    if (refreshTime <= 0) {
      const refreshed = await this.refreshToken(tokens, tokenType)
      return refreshed.tokens
    } else {
      setTimeout(() => {
        this.refreshToken(tokens, tokenType)
      }, refreshTime)
      return tokens
    }
  }

  async setRefreshToken (tokens) {
    const refreshedTokens = {
      ...tokens
    }
    if (tokens.farmapremium.access_token) {
      refreshedTokens.farmapremium = await this.scheduleRefreshToken(tokens.farmapremium, 'farmapremium')
    }
    if (tokens.farmaoffice.access_token) {
      refreshedTokens.farmaoffice = await this.scheduleRefreshToken(tokens.farmaoffice, 'farmaoffice')
    }
    return refreshedTokens
  }

  async getAuthData (jti, removeConfigCache = false) {
    try {
      // TODO: create methid with JTI renew
      // If has JTI, refresh session
      if (jti) await axios.create().put(URLS.NEXTERA_UPDATE_TOKEN_URL, { jti }, { withCredentials: true })
      // get session data
      let tokens = await this.fetchToken()
      if (!tokens?.farmacloud?.jti) {
        this.redirectLogin({ loginUrl: tokens.authorization_url.authorization_url })
        throw new Error('Unauthorized')
      }
      tokens = await this.setRefreshToken(tokens)
      const { micros, sidebar, user } = await this.fetchShellConfig(tokens.farmapremium.access_token, removeConfigCache)

      // create a common data structure
      const permissions = {}
      micros.forEach(element => {
        permissions[element.id] = element.active
      })
      const userData = {
        name: [user.first_name, user.last_name].filter(Boolean).join(' '),
        short_name: user.short_name,
        role: user.fp_organization_type || 'store',
        email: user.email,
        language: 'es',
        requestLang: user.locale || 'es',
        is_admin: user.fp_organization_type === 'adm1n' || false,
        permissions,
        userUuid: user.uuid,
        organizationUuid: user.organization
      }
      const authData = {
        isLogged: true,
        tokens,
        user: { ...user, ...userData },
        micros,
        sidebar
      }
      AuthStore.storeUserData(userData)
      this.initExternalTools(authData)
      return authData
    } catch (error) {
      return this.manageTokenError()
    }
  }

  initExternalTools (authData) {
    this.initSentry(authData)
  }

  initUsetifull (authData) {
    const firstName = AuthStore.getFirstname()
    const role = AuthStore.getIsAdmin() ? 'adm1n' : 'pharmacy'
    const micros = authData.micros
      .filter((m) => m.active).map((m) => m.id)
      .join(', ') || 'null'
    const plan = Object.entries(authData.agreementConfig)
      .filter((e) => e[1]?.enabled === false).map(([key]) => key)
      .join(', ') || 'null'
    window.initUsetiful({ role, firstName, micros, plan })
  }

  initSentry (authData) {
    if (sentryEnv) {
      const userUuid = authData.user.userUuid
      const organizationUuid = authData.user.organizationUuid

      const sentryUser = {
        available_languages: authData.user.available_languages.data.map((lang) => lang.locale),
        name: authData.user.name,
        locale: authData.user.locale,
        organizationUuid,
        role_name: authData.user.role_name,
        userUuid
      }
      Sentry.setUser(sentryUser)

      // clarity link
      const customSessionId = getSessionId(organizationUuid)
      if (window.clarity) {
        function logClarityIds () {
          console.table({ userUuid, customSessionId })
        }
        window.logClarityIds = logClarityIds
        window.clarity('identify', userUuid, customSessionId)
        Sentry.setTag('clarity_custom_id', userUuid)
        Sentry.setTag('clarity_custom_session_id', customSessionId)
      }
    }
  }
}
